import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Grid, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import { postApiCall } from 'api/apiService';
import { FORGOT_PASSWORD, OTP_VERIFICATION } from 'constant/app.config';
import Loading from 'assets/images/loading.png';

function SendOTP({
  setShowForgotPassword,
  handleShowNewPassword,
  setShowsendOTP,
  EmailId,
  setotpResponseObj,
}) {
  const [otp, setOtp] = useState(Array(4).fill('')); // 4-digit OTP
  const [timeLeft, setTimeLeft] = useState(60); // Timer starts at 60 seconds
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesmd = useMediaQuery(theme.breakpoints.down('md'));

  // Timer logic
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer); // Cleanup interval on unmount
    }
  }, [timeLeft]);

  // Handle OTP input changes
  const handleChange = (value, index) => {
    if (/^\d$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to the next input field
      if (index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === '') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
    }
  };

  // Handle Backspace navigation
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && otp[index] === '') {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  // Handle pasting OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4); // Limit to 4 digits
    if (/^\d{1,4}$/.test(pastedData)) {
      const newOtp = pastedData
        .split('')
        .concat(Array(4 - pastedData.length).fill(''));
      setOtp(newOtp);

      // Automatically move focus to the last filled field
      const nextIndex = Math.min(newOtp.length - 1, 3);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  // const [verify, setVerify] = useState('')

  // Submit OTP
  const handleSubmit = async () => {
    const enteredOtp = otp.join(''); // Combine the OTP array into a string

    if (enteredOtp.length !== 4) {
      setErrorMessage('Please enter a valid 4-digit OTP.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await postApiCall(OTP_VERIFICATION, {
        email: EmailId,
        otp: enteredOtp,
      });
      if (response?.data?.status === 1) {
        handleShowNewPassword();
        setotpResponseObj(response?.data);
        setShowForgotPassword(false);
        setShowsendOTP(false);
      } else {
        setErrorMessage(
          response?.data?.message || 'Invalid OTP. Please try again.'
        );
      }
    } catch (error) {
      setErrorMessage('Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setOtp(Array(4).fill('')); // Clear OTP fields
    setTimeLeft(60); // Reset timer to 60 seconds
    setErrorMessage('');

    try {
      setIsLoading(true);
      const response = await postApiCall(FORGOT_PASSWORD, { email: EmailId });
      if (response?.data?.status === 1) {
      } else {
        setErrorMessage(
          response?.data?.message || 'Failed to resend OTP. Please try again.'
        );
      }
    } catch (error) {
      setErrorMessage('Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isLoading) {
      document.body.style.pointerEvents = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'left',
          minHeight: '10vh',
        }}
      >
        <Grid
          container
          sx={{
            maxWidth: '900px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'left',
            gap: '20px',
            marginLeft: matchessm ? '0px' : matchesmd ? -2.5 : '',
          }}
        >
          {/* OTP Input Boxes */}
          <Grid sx={{ display: 'flex', gap: '10px' }} onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                autoFocus={index === 0}
                type="text"
                value={digit}
                maxLength="1"
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => (inputRefs.current[index] = el)}
                style={{
                  width: matchessm ? '40px' : '60px',
                  height: matchessm ? '40px' : '60px',
                  textAlign: 'center',
                  fontSize: '18px',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                }}
              />
            ))}
          </Grid>

          {/* Error Message */}
          {errorMessage && (
            <Typography sx={{ color: '#D42F2F', fontWeight: 600 }}>
              {errorMessage}
            </Typography>
          )}

          {/* Timer */}
          {/* Timer */}
          {timeLeft > 0 && (
            <Typography
              sx={{
                color: 'black', // Permanent black color for the label "Time left"
                fontFamily: 'poppins',
                margin: '10px 0',
              }}
            >
              Time left:{' '}
              <span
                style={{
                  color: timeLeft <= 20 ? '#D42F2F' : 'black',
                  fontWeight: 600,
                  fontFamily: 'poppins',
                }}
              >
                {formatTime()}
              </span>
            </Typography>
          )}

          {/* Submit and Resend OTP Buttons */}
          <Grid sx={{ display: 'flex', gap: 3 }}>
            {otp.every((digit) => digit) && timeLeft > 0 && (
              <Button
                variant="outlined"
                sx={{
                  background: '#4CA4D6',
                  color: '#FFFFFF',
                  '&:hover': { background: '#3b92be' },
                }}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Submitting' : 'Submit'}
              </Button>
            )}

            {isLoading && (
              <img
                alt="Loader"
                src={Loading}
                className="loaderImage"
                style={{ height: '40px', width: '40px' }} // 🔹 smaller & aligned
              />
            )}
            {timeLeft === 0 && (
              <Typography
                onClick={handleResendOTP}
                sx={{
                  cursor: 'pointer',
                  color: '#D42F2F',
                  fontWeight: 600,
                  fontFamily: 'poppins',
                }}
              >
                Resend OTP
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default SendOTP;
